import React, {useEffect, useState} from "react";
import {useActions} from "../../hook/useActions";
import {useTypedSelector} from "../../hook/useTypedSelector";
import { Editor } from "react-draft-wysiwyg";
import {convertFromRaw, convertToRaw, EditorState} from 'draft-js';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { convertToHTML } from 'draft-convert';
import DOMPurify from "dompurify";
import {useTranslation} from "react-i18next";

const MainPageEditor: React.FC = () => {
    const { GetMainPageTextAsync, UpdateMainPageTextAsync } = useActions();
    const { mainPageText } = useTypedSelector((s) => s.pagesConfigReducer);
    const { user } = useTypedSelector((s) => s.user);

    const [editorState, setEditorState] = useState(
        EditorState.createEmpty(),
    );
    const [convertedContent, setConvertedContent] = useState<string>("");
    const [lastHtml, setLastHtml] = useState<string>("");
    // ADD: control collapsed/expanded preview
    const [showMore, setShowMore] = useState<boolean>(false);

    const {t} = useTranslation();

    useEffect(() => {
        const html = convertToHTML({
            blockToHTML: (block) => {
                const textAlignRaw = block.data?.['text-align'];

                if (textAlignRaw) {
                    return {
                        start: `<p style="text-align:${textAlignRaw}">`,
                        end: '</p>',
                    };
                }

                return undefined;
            },

            entityToHTML: (entity, originalText) => {
                if (entity.type === 'LINK') {
                    const { url, target } = entity.data;

                    return (
                        <a
                            href={url}
                            target={target || '_blank'}
                            rel="noopener noreferrer"
                        >
                            {originalText}
                        </a>
                    );
                }

                return originalText;
            },

            styleToHTML: (style: string) => {
                if (style.startsWith('color-')) {
                    const color = style.replace('color-', '');
                    return <span style={{ color }} />;
                }

                if (style.startsWith('fontsize-')) {
                    const size = style.replace('fontsize-', '');
                    return <span style={{ fontSize: `${size}px` }} />;
                }

                if (style.startsWith('fontfamily-')) {
                    const font = style.replace('fontfamily-', '');
                    return <span style={{ fontFamily: font }} />;
                }

                return undefined;
            },
        })(editorState.getCurrentContent());

        setConvertedContent(html);
    }, [editorState]);

    useEffect(() => {
        // load content from store when available
        if (mainPageText && mainPageText !== "") {
            try {
                const contentState = convertFromRaw(JSON.parse(mainPageText));
                setEditorState(EditorState.createWithContent(contentState));
                // also set lastHtml to current serialized HTML so button shows Saved when appropriate
                const initialHtml = convertToHTML({})(EditorState.createWithContent(contentState).getCurrentContent());
                setLastHtml(initialHtml);
            } catch (e) {
                // invalid saved content — ignore
            }
        } else {
            // ensure action to fetch if empty
            GetMainPageTextAsync();
        }
    }, [mainPageText]);

    function assertSafeHTML(raw: string): string {
        const sanitized = DOMPurify.sanitize(raw, {
            USE_PROFILES: { html: true },
        });

        return sanitized;
    };

    // ADD: return the first block element's outerHTML or fallback to the first text line wrapped in <p>
    function getFirstBlockHTML(html: string): string {
        if (!html) return "";
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const body = doc.body;
            // prefer first element child (e.g., first <p>, <h1>, etc.)
            const firstEl = Array.from(body.childNodes).find(n => n.nodeType === Node.ELEMENT_NODE) as Element | undefined;
            if (firstEl) {
                return firstEl.outerHTML;
            }
            // fallback to first line of text content
            const text = body.textContent || "";
            const firstLine = text.split(/\r?\n/).map(l => l.trim()).find(l => l.length > 0) || "";
            return firstLine ? `<p>${firstLine}</p>` : "";
        } catch (e) {
            // parsing failed — fallback to raw truncated text
            const plain = html.replace(/<\/?[^>]+(>|$)/g, ""); // strip tags
            const firstLine = plain.split(/\r?\n/).map(l => l.trim()).find(l => l.length > 0) || "";
            return firstLine ? `<p>${firstLine}</p>` : "";
        }
    }

    function saveContent() {
        const contentState = editorState.getCurrentContent();
        const rawContent = convertToRaw(contentState);
        const serialized = JSON.stringify(rawContent);
        UpdateMainPageTextAsync(serialized);
        setLastHtml(convertedContent);
    }

    // compute sanitized full and first-block HTML
    const safeFullHtml = assertSafeHTML(convertedContent || "");
    const firstBlockHtml = getFirstBlockHTML(safeFullHtml);
    const hasMore = firstBlockHtml !== safeFullHtml && safeFullHtml.trim() !== "";

    return (
        <>
            <div
                className="preview"
                style={{width:"482px"}}
                // show either first block or full html depending on showMore
                dangerouslySetInnerHTML={{__html: showMore || !hasMore ? safeFullHtml : firstBlockHtml}}></div>

            {/* toggle only when there's more content */}
            {hasMore &&
                <div style={{textAlign: "right", margin: "0 0 10px 0"}}>
                    <button onClick={() => setShowMore(prev => !prev)} style={{background: "transparent", border: "none", color: "#036", cursor: "pointer"}}>
                        {showMore ? t("TextEditor.ShowLess") : t("TextEditor.ShowMore")}
                    </button>
                </div>
            }

            {user && user.role && user.role.includes("Admin") &&
                <div style={{background: "#fff", border: "1px solid #ccc"}}>
                    <Editor
                        editorState={editorState}
                        onEditorStateChange={setEditorState}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"

                        toolbar={{
                            fontFamily: {
                                options: ["Inter", "Arial", "Georgia", "Impact", "Tahoma", "Times New Roman", "Verdana", "Advent Pro"],
                            },
                            fontSize: {
                                options: [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 36, 48, 60, 72, 96],
                            }
                        }}
                    />
                    {convertedContent === lastHtml ?
                        <button style={{width:"100%", height:"50px", background: "linear-gradient(180deg, rgba(200, 255, 200, 0.8) 0%, rgba(100, 255, 100, 0.8) 100%)", cursor: "default"}}
                        >Saved!</button>
                        :
                        <button style={{width:"100%", height:"50px", background: "linear-gradient(180deg, rgba(188, 235, 254, 0.8) 0%, rgba(3, 87, 139, 0.8) 100%)"}}
                                onClick={saveContent}>Save</button>
                    }
                </div>}
        </>
    );
};

export default MainPageEditor;

